<script lang="ts">
    import { onMount } from 'svelte';
    import Limiter from './_wrappers/Limiter.svelte';
    import Transition from './_wrappers/Transition.svelte';
    import Image from './Image/Image.svelte';
    import debug from 'debug';
    import { DEFAULT_LAYOUT } from '$lib/components/constants';

    // Utils
    import createSliderController from './_utils/sliderLogic';
    import { getWidthByScreenSize } from './_utils/getWidthByScreenSize';
    import { getBorderRadius } from '$lib/global/helper/border-radius';

    // Types
    import type { ImageSize } from '$lib/types/Components/SharedTypes';
    import type { TransitionEffect } from '$lib/types/Theme';

    const debugLog = debug('components:ImageSlider');

    export let bgColor = 'transparent';
    export let images: string[] = [];
    export let size: ImageSize = 'large';
    export let framed = false;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let layout = DEFAULT_LAYOUT;

    const isInColumn = layout.isInColumn;

    const imageBorderRadiusClass = getBorderRadius('sliderImage');

    let offset = 0;

    // filter images with invalid URLs
    const filteredImages = images.filter((image) => {
        try {
            new URL(image);

            return true;
        } catch (error) {
            debugLog(`Invalid image URL: ${image}`);

            return false;
        }
    });

    const { start, next, back, ...sliderController } = createSliderController(filteredImages);

    sliderController.offset.subscribe((newOffset) => {
        offset = newOffset;
    });

    onMount(() => {
        start();
    });
</script>

<div class="w-full" class:h-full={!isInColumn} style="background-color: {bgColor}">
    <Limiter>
        {#if filteredImages.length === 1}
            <div class={framed ? 'p-6 sm:px-12 sm:py-6 md:px-20 md:py-10' : 'p-0'}>
                <div
                    class={`${imageBorderRadiusClass} relative w-full translate-y-0 transform overflow-hidden`}
                >
                    <Image {size} src={filteredImages[0]} fixedMaxWidth={getWidthByScreenSize()} />
                </div>
            </div>
        {:else}
            <Transition delay={transitionDelay} effect={transitionEffect}>
                <div class={framed ? 'p-6 sm:px-12 sm:py-6 md:px-20 md:py-10' : 'p-0'}>
                    <div
                        class={`${imageBorderRadiusClass} relative w-full translate-y-0 transform overflow-hidden`}
                    >
                        <button
                            on:click={back}
                            class="absolute bottom-0 left-0 top-0 z-30 w-1/2 cursor-pointer"
                        />
                        <button
                            on:click={next}
                            class="absolute bottom-0 right-0 top-0 z-30 w-1/2 cursor-pointer"
                        />
                        <div
                            class="absolute bottom-3 left-0 right-0 z-20 flex w-full justify-between space-x-1 px-3"
                        >
                            {#each filteredImages as image}
                                <div
                                    class:bg-white={filteredImages.indexOf(image) < offset}
                                    class:bg-gray-400={filteredImages.indexOf(image) >= offset}
                                    class="h-1 w-full flex-grow overflow-hidden rounded-sm"
                                >
                                    <div
                                        style="animation-fill-mode: forwards"
                                        class="h-full w-0 bg-white"
                                        class:animate-fill-default={filteredImages.indexOf(
                                            image,
                                        ) === offset}
                                    />
                                </div>
                            {/each}
                        </div>
                        <div
                            class="absolute inset-x-0 bottom-0 z-10 h-16 bg-gradient-to-t from-black/40 to-black/0"
                        />
                        <div
                            class="flex transition duration-700"
                            style="width: calc({filteredImages.length} * 100%); transform: translateX(calc({offset} * (-100% / {filteredImages.length})))"
                        >
                            {#each filteredImages as image}
                                <div class="w-full">
                                    <Image
                                        {size}
                                        src={image}
                                        fixedMaxWidth={getWidthByScreenSize()}
                                    />
                                </div>
                            {/each}
                        </div>
                    </div>
                </div>
            </Transition>
        {/if}
    </Limiter>
</div>
